// Guest lesson-preview block trimming — ported from composable
// `learning-center-module/utils.js` (SFCC-11505). For a logged-out learner the
// lesson renders only a teaser: the first `maxTextComponents` Builder "Text"
// blocks, each truncated to `charLimit` characters. This LIMITS the content
// delivered to the DOM (a real gate), not just a CSS clip.

interface BuilderBlock {
  component?: { name?: string; options?: { text?: string } };
  children?: BuilderBlock[];
}

/** Plain-text length of an HTML string (browser only; 0 on the server). */
const htmlTextLength = (html: string): number => {
  if (typeof document === "undefined") return html.length;
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").length;
};

/** Plain text (truncated) of an HTML string. */
const truncateHtmlText = (html: string, limit: number): string => {
  if (typeof document === "undefined") return `${html.slice(0, limit)}...`;
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  return `${text.slice(0, limit)}...`;
};

/**
 * Collect the first `maxTextComponents` Text blocks (depth-first), truncating
 * any whose plain text exceeds `charLimit`. Returns a NEW array; truncated
 * blocks are deep-cloned so the source content is never mutated.
 */
export function getPreviewBlocks(
  blocks: BuilderBlock[],
  maxTextComponents = 1,
  charLimit = 550,
): BuilderBlock[] {
  const preview: BuilderBlock[] = [];

  const traverse = (block: BuilderBlock): void => {
    if (preview.length >= maxTextComponents) return;

    if (block.component?.name === "Text") {
      const html = block.component.options?.text ?? "";
      if (htmlTextLength(html) > charLimit) {
        const cloned = JSON.parse(JSON.stringify(block)) as BuilderBlock;
        if (cloned.component?.options) {
          cloned.component.options.text = `<p>${truncateHtmlText(html, charLimit)}</p>`;
        }
        preview.push(cloned);
      } else {
        preview.push(block);
      }
      return;
    }

    for (const child of block.children ?? []) {
      traverse(child);
      if (preview.length >= maxTextComponents) break;
    }
  };

  for (const block of blocks) {
    traverse(block);
    if (preview.length >= maxTextComponents) break;
  }

  return preview;
}
